import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import api from '@/lib/api';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import MenuImport from '@/components/MenuImport';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  FolderOpen,
  UtensilsCrossed,
  Sparkles,
  Euro,
  Image as ImageIcon,
  Link as LinkIcon,
  Upload,
  Star,
  AlertTriangle,
  ShoppingBag,
  Wand2,
  Clock,
  BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL (handles both relative and absolute URLs)
const getImageUrl = (url) => {
  if (!url) return null;
  // If it's a relative API URL, prefix with backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  // If it already has the backend URL or is external, return as-is
  return url;
};

const MenuManagement = () => {
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [allergensList, setAllergensList] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Multi-menu
  const [menus, setMenus] = useState([]);
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm, setMenuForm] = useState({ name: '', order: 0, time_slots: [] });
  const [selectedMenuFilter, setSelectedMenuFilter] = useState('all');
  
  // Category form
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', order: 0, menu_ids: [] });

  // Daily special
  const [dailySpecial, setDailySpecial] = useState(null);
  const [dailyDialogOpen, setDailyDialogOpen] = useState(false);
  const [dailyForm, setDailyForm] = useState({ title: 'Menu du jour', description: '', price: '', dish_ids: [], active: true });
  const [suggestingDaily, setSuggestingDaily] = useState(false);
  
  // Dish form
  const [dishDialogOpen, setDishDialogOpen] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [dishForm, setDishForm] = useState({
    name: '', description: '', price: '', price_takeaway: '', category_id: '', menu_ids: [], photo_url: '', available: true, margin: '', allergens: [], upsells: []
  });
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingUpsells, setGeneratingUpsells] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAllergens();
  }, []);

  const fetchAllergens = async () => {
    try {
      const res = await api.get('/allergens');
      setAllergensList(res.data);
    } catch (error) {
      console.error('Error fetching allergens:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [catRes, dishRes, menuRes, dailyRes] = await Promise.all([
        api.get('/categories'),
        api.get('/dishes'),
        api.get('/menus'),
        api.get('/daily-special')
      ]);
      setCategories(catRes.data);
      setDishes(dishRes.data);
      setMenus(menuRes.data);
      setDailySpecial(dailyRes.data?.id ? dailyRes.data : null);
      if (catRes.data.length > 0 && !selectedCategory) {
        setSelectedCategory(catRes.data[0].id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du menu');
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, description: category.description || '', order: category.order, menu_ids: category.menu_ids || [] });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', order: categories.length, menu_ids: [] });
    }
    setCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }
    setSaving(true);
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, categoryForm);
        toast.success('Catégorie mise à jour');
      } else {
        await api.post('/categories', categoryForm);
        toast.success('Catégorie créée');
      }
      fetchData();
      setCategoryDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie et tous ses plats ?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Catégorie supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Menu handlers
  const DAYS_OPTIONS = [
    { value: 'lundi', label: 'Lun' }, { value: 'mardi', label: 'Mar' },
    { value: 'mercredi', label: 'Mer' }, { value: 'jeudi', label: 'Jeu' },
    { value: 'vendredi', label: 'Ven' }, { value: 'samedi', label: 'Sam' },
    { value: 'dimanche', label: 'Dim' }
  ];

  const openMenuDialog = (menu = null) => {
    if (menu) {
      setEditingMenu(menu);
      setMenuForm({ name: menu.name, order: menu.order, time_slots: menu.time_slots || [] });
    } else {
      setEditingMenu(null);
      setMenuForm({ name: '', order: menus.length, time_slots: [{ days: [], start_hour: '11:00', end_hour: '15:00' }] });
    }
    setMenuDialogOpen(true);
  };

  const addTimeSlot = () => {
    setMenuForm(prev => ({ ...prev, time_slots: [...prev.time_slots, { days: [], start_hour: '18:00', end_hour: '23:00' }] }));
  };

  const removeTimeSlot = (index) => {
    setMenuForm(prev => ({ ...prev, time_slots: prev.time_slots.filter((_, i) => i !== index) }));
  };

  const updateTimeSlot = (index, field, value) => {
    setMenuForm(prev => ({
      ...prev,
      time_slots: prev.time_slots.map((slot, i) => i === index ? { ...slot, [field]: value } : slot)
    }));
  };

  const toggleSlotDay = (slotIndex, day) => {
    setMenuForm(prev => ({
      ...prev,
      time_slots: prev.time_slots.map((slot, i) => {
        if (i !== slotIndex) return slot;
        const days = slot.days.includes(day) ? slot.days.filter(d => d !== day) : [...slot.days, day];
        return { ...slot, days };
      })
    }));
  };

  const saveMenu = async () => {
    if (!menuForm.name.trim()) { toast.error('Le nom du menu est requis'); return; }
    setSaving(true);
    try {
      if (editingMenu) {
        await api.put(`/menus/${editingMenu.id}`, menuForm);
        toast.success('Menu mis à jour');
      } else {
        await api.post('/menus', menuForm);
        toast.success('Menu créé');
      }
      fetchData();
      setMenuDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const deleteMenu = async (id) => {
    if (!window.confirm('Supprimer ce menu ? Les catégories ne seront pas supprimées.')) return;
    try {
      await api.delete(`/menus/${id}`);
      toast.success('Menu supprimé');
      if (selectedMenuFilter === id) setSelectedMenuFilter('all');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleCategoryMenu = (menuId) => {
    setCategoryForm(prev => ({
      ...prev,
      menu_ids: prev.menu_ids.includes(menuId) ? prev.menu_ids.filter(id => id !== menuId) : [...prev.menu_ids, menuId]
    }));
  };

  // Daily special handlers
  const openDailyDialog = () => {
    if (dailySpecial) {
      setDailyForm({
        title: dailySpecial.title || 'Menu du jour',
        description: dailySpecial.description || '',
        price: dailySpecial.price?.toString() || '',
        dish_ids: dailySpecial.dish_ids || [],
        active: dailySpecial.active !== false
      });
    } else {
      setDailyForm({ title: 'Menu du jour', description: '', price: '', dish_ids: [], active: true });
    }
    setDailyDialogOpen(true);
  };

  const toggleDailyDish = (dishId) => {
    setDailyForm(prev => ({
      ...prev,
      dish_ids: prev.dish_ids.includes(dishId) ? prev.dish_ids.filter(id => id !== dishId) : [...prev.dish_ids, dishId]
    }));
  };

  const saveDaily = async () => {
    if (!dailyForm.title.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      const data = { ...dailyForm, price: dailyForm.price ? parseFloat(dailyForm.price) : null };
      if (dailySpecial?.id) {
        await api.put(`/daily-special/${dailySpecial.id}`, data);
        toast.success('Menu du jour mis à jour');
      } else {
        await api.post('/daily-special', data);
        toast.success('Menu du jour créé');
      }
      fetchData();
      setDailyDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const deleteDaily = async () => {
    if (!dailySpecial?.id) return;
    if (!window.confirm('Supprimer le menu du jour ?')) return;
    try {
      await api.delete(`/daily-special/${dailySpecial.id}`);
      toast.success('Menu du jour supprimé');
      setDailySpecial(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const suggestDaily = async () => {
    setSuggestingDaily(true);
    try {
      const response = await api.post('/ai/suggest-daily-special');
      const s = response.data;
      setDailyForm({
        title: s.title || 'Menu du jour',
        description: s.description || '',
        price: s.price?.toString() || '',
        dish_ids: s.dish_ids || [],
        active: true
      });
      toast.success('Suggestion IA appliquée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de suggestion');
    } finally { setSuggestingDaily(false); }
  };

  // Dish handlers
  const openDishDialog = (dish = null) => {
    if (dish) {
      setEditingDish(dish);
      setDishForm({
        name: dish.name,
        description: dish.description || '',
        price: dish.price.toString(),
        price_takeaway: dish.price_takeaway ? dish.price_takeaway.toString() : '',
        category_id: dish.category_id,
        menu_ids: dish.menu_ids || [],
        photo_url: dish.photo_url || '',
        available: dish.available,
        margin: dish.margin?.toString() || '',
        allergens: dish.allergens || [],
        upsells: dish.upsells || []
      });
    } else {
      setEditingDish(null);
      setDishForm({
        name: '',
        description: '',
        price: '',
        price_takeaway: '',
        category_id: selectedCategory || (categories[0]?.id || ''),
        menu_ids: [],
        photo_url: '',
        available: true,
        margin: '',
        allergens: [],
        upsells: []
      });
    }
    setDishDialogOpen(true);
  };

  const toggleAllergen = (allergenCode) => {
    setDishForm(prev => ({
      ...prev,
      allergens: prev.allergens.includes(allergenCode)
        ? prev.allergens.filter(a => a !== allergenCode)
        : [...prev.allergens, allergenCode]
    }));
  };

  const toggleDishMenu = (menuId) => {
    setDishForm(prev => ({
      ...prev,
      menu_ids: prev.menu_ids.includes(menuId)
        ? prev.menu_ids.filter(id => id !== menuId)
        : [...prev.menu_ids, menuId]
    }));
  };

  const toggleUpsell = (dishId) => {
    setDishForm(prev => ({
      ...prev,
      upsells: prev.upsells.includes(dishId)
        ? prev.upsells.filter(id => id !== dishId)
        : [...prev.upsells, dishId]
    }));
  };

  const generateUpsells = async () => {
    if (!editingDish) {
      toast.error('Enregistrez d\'abord le plat');
      return;
    }
    setGeneratingUpsells(true);
    try {
      const response = await api.post('/ai/suggest-upsells', { dish_id: editingDish.id });
      if (response.data.upsell_ids && response.data.upsell_ids.length > 0) {
        setDishForm(prev => ({ ...prev, upsells: response.data.upsell_ids }));
        toast.success('Suggestions générées par l\'IA !');
      } else {
        toast.info('Aucune suggestion trouvée');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingUpsells(false);
    }
  };

  const saveDish = async () => {
    if (!dishForm.name.trim() || !dishForm.price || !dishForm.category_id) {
      toast.error('Nom, prix et catégorie sont requis');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...dishForm,
        price: parseFloat(dishForm.price),
        price_takeaway: dishForm.price_takeaway ? parseFloat(dishForm.price_takeaway) : null,
        margin: dishForm.margin ? parseFloat(dishForm.margin) : null
      };
      if (editingDish) {
        await api.put(`/dishes/${editingDish.id}`, data);
        toast.success('Plat mis à jour');
      } else {
        await api.post('/dishes', data);
        toast.success('Plat créé');
      }
      fetchData();
      setDishDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteDish = async (id) => {
    if (!window.confirm('Supprimer ce plat ?')) return;
    try {
      await api.delete(`/dishes/${id}`);
      toast.success('Plat supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleDishAvailability = async (dish) => {
    try {
      await api.put(`/dishes/${dish.id}`, { ...dish, available: !dish.available });
      fetchData();
      toast.success(dish.available ? 'Plat désactivé' : 'Plat activé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleDishFeatured = async (dish) => {
    try {
      const response = await api.put(`/dishes/${dish.id}/toggle-featured`);
      fetchData();
      toast.success(response.data.is_featured ? 'Plat ajouté aux populaires' : 'Plat retiré des populaires');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateDescription = async () => {
    if (!dishForm.name.trim()) {
      toast.error('Entrez d\'abord le nom du plat');
      return;
    }
    setGeneratingDescription(true);
    try {
      const category = categories.find(c => c.id === dishForm.category_id);
      const response = await api.post('/ai/generate-description', {
        dish_name: dishForm.name,
        dish_category: category?.name || 'Plat'
      });
      setDishForm(prev => ({ ...prev, description: response.data.description }));
      toast.success('Description générée !');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const featuredCount = dishes.filter(d => d.is_featured).length;

  // Filter categories by selected menu
  const filteredCategories = selectedMenuFilter === 'all' 
    ? categories 
    : categories.filter(c => (c.menu_ids || []).length === 0 || (c.menu_ids || []).includes(selectedMenuFilter));

  const filteredDishes = selectedCategory 
    ? dishes.filter(d => d.category_id === selectedCategory)
    : selectedMenuFilter === 'all'
      ? dishes
      : dishes.filter(d => {
          const cat = categories.find(c => c.id === d.category_id);
          return !cat || (cat.menu_ids || []).length === 0 || (cat.menu_ids || []).includes(selectedMenuFilter);
        });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid md:grid-cols-4 gap-6">
          <Skeleton className="h-96" />
          <div className="md:col-span-3 space-y-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="menu-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">
            Gestion du Menu
          </h1>
          <p className="text-gray-500 mt-1">
            {categories.length} catégorie(s), {dishes.length} plat(s)
            {featuredCount > 0 && (
              <span className="ml-2 text-orange-500">
                • {featuredCount} populaire(s)
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            className="rounded-full border-orange-200 text-orange-600 hover:bg-orange-50"
            data-testid="import-menu-btn"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Import IA
          </Button>
          <Button 
            variant="outline" 
            onClick={() => openMenuDialog()}
            className="rounded-full"
            data-testid="add-menu-btn"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            Menu
          </Button>
          <Button 
            variant="outline" 
            onClick={() => openCategoryDialog()}
            className="rounded-full"
            data-testid="add-category-btn"
          >
            <FolderOpen className="mr-2 h-4 w-4" />
            Catégorie
          </Button>
          <Button 
            onClick={() => openDishDialog()}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
            data-testid="add-dish-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Ajouter un plat
          </Button>
        </div>
      </div>

      {/* Multi-menu tabs */}
      {menus.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setSelectedMenuFilter('all')}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedMenuFilter === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="menu-filter-all"
          >
            Tous
          </button>
          {menus.map(menu => (
            <div key={menu.id} className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setSelectedMenuFilter(menu.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedMenuFilter === menu.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`menu-filter-${menu.id}`}
              >
                <BookOpen className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                {menu.name}
              </button>
              <button onClick={() => openMenuDialog(menu)} className="p-1 text-gray-400 hover:text-orange-500">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => deleteMenu(menu.id)} className="p-1 text-gray-400 hover:text-red-500">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Info banner for featured dishes */}
      {featuredCount === 0 && dishes.length > 0 && (
        <Card className="border-orange-200 bg-orange-50 border">
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <Star className="h-5 w-5 text-orange-500" />
            <p className="text-sm text-orange-800">
              <strong>Astuce :</strong> Cliquez sur l'étoile pour ajouter des plats à la section "Populaires" de votre menu public.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Daily Special Card */}
      <Card className="border-0 shadow-soft" data-testid="daily-special-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <UtensilsCrossed className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-gray-900">
                  {dailySpecial ? dailySpecial.title : 'Menu du jour'}
                </h3>
                <p className="text-xs text-gray-500">
                  {dailySpecial 
                    ? `${dailySpecial.price ? dailySpecial.price.toFixed(2) + '€' : ''} · ${(dailySpecial.dishes || []).length} plat(s) · Expire demain`
                    : 'Aucun menu du jour actif'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {dailySpecial && (
                <Button variant="ghost" size="sm" onClick={deleteDaily} className="text-red-500 hover:text-red-600 h-8 w-8 p-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button 
                onClick={openDailyDialog}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full h-9 px-4 text-sm"
                data-testid="edit-daily-btn"
              >
                {dailySpecial ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Categories sidebar */}
        <Card className="lg:col-span-1 border-0 shadow-soft h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">Catégories</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                !selectedCategory 
                  ? 'bg-orange-50 text-orange-600' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="category-all"
            >
              Tous les plats ({dishes.length})
            </button>
            {filteredCategories.map((category) => (
              <div key={category.id} className="group flex items-center">
                <button
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-1 text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id 
                      ? 'bg-orange-50 text-orange-600' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  {category.name} ({dishes.filter(d => d.category_id === category.id).length})
                </button>
                <div className="opacity-0 group-hover:opacity-100 flex gap-1 pr-1 transition-opacity">
                  <button 
                    onClick={() => openCategoryDialog(category)}
                    className="p-1 text-gray-400 hover:text-orange-500"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button 
                    onClick={() => deleteCategory(category.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {categories.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                Aucune catégorie. Créez-en une pour commencer.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Dishes list */}
        <div className="lg:col-span-3 space-y-4">
          {filteredDishes.length === 0 ? (
            <Card className="border-0 shadow-soft">
              <CardContent className="py-12 text-center">
                <UtensilsCrossed className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
                  Aucun plat
                </h3>
                <p className="text-gray-500 mb-4">
                  {categories.length === 0 
                    ? "Créez d'abord une catégorie, puis ajoutez vos plats."
                    : "Commencez par ajouter votre premier plat."}
                </p>
                <Button 
                  onClick={() => openDishDialog()}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
                  disabled={categories.length === 0}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter un plat
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredDishes.map((dish) => (
              <Card 
                key={dish.id} 
                className={`border-0 shadow-soft hover:shadow-hover transition-all ${!dish.available ? 'opacity-60' : ''}`}
                data-testid={`dish-card-${dish.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Image */}
                    <div className="flex-shrink-0 relative">
                      {dish.photo_url ? (
                        <img 
                          src={getImageUrl(dish.photo_url)} 
                          alt={dish.name}
                          className="w-24 h-24 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-gray-300" />
                        </div>
                      )}
                      {/* Featured star button */}
                      <button
                        onClick={() => toggleDishFeatured(dish)}
                        className={`absolute -top-2 -right-2 h-7 w-7 rounded-full flex items-center justify-center shadow-md transition-all ${
                          dish.is_featured 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-white text-gray-400 hover:text-orange-500'
                        }`}
                        title={dish.is_featured ? 'Retirer des populaires' : 'Ajouter aux populaires'}
                        data-testid={`featured-${dish.id}`}
                      >
                        <Star className={`h-4 w-4 ${dish.is_featured ? 'fill-current' : ''}`} />
                      </button>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-heading font-semibold text-lg text-gray-900 flex items-center gap-2 flex-wrap">
                            {dish.name}
                            {dish.is_featured && (
                              <Badge className="bg-orange-100 text-orange-600 text-xs border-0">Populaire</Badge>
                            )}
                            {!dish.available && (
                              <Badge variant="secondary" className="text-xs">Indisponible</Badge>
                            )}
                          </h3>
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                            {dish.description || 'Pas de description'}
                          </p>
                          {/* Allergens display */}
                          {dish.allergens && dish.allergens.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              <AlertTriangle className="h-3 w-3 text-amber-500" />
                              <span className="text-xs text-amber-600">
                                {dish.allergens.map(a => allergensList[a] || a).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-heading font-bold text-lg text-orange-500 whitespace-nowrap">
                            {dish.price.toFixed(2)}€
                          </p>
                          {dish.price_takeaway != null && (
                            <p className="text-xs text-gray-400 whitespace-nowrap">
                              Emporter : {dish.price_takeaway.toFixed(2)}€
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span>{dish.views || 0} vues</span>
                          <span>•</span>
                          <span>{dish.clicks || 0} clics</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={dish.available}
                            onCheckedChange={() => toggleDishAvailability(dish)}
                            data-testid={`toggle-${dish.id}`}
                          />
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openDishDialog(dish)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => deleteDish(dish.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Nom</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Entrées, Plats, Desserts..."
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-desc">Description (optionnel)</Label>
              <Textarea
                id="cat-desc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description de la catégorie"
                rows={2}
              />
            </div>
            {menus.length > 0 && (
              <div className="space-y-2">
                <Label>Afficher dans les menus</Label>
                <p className="text-xs text-gray-500">Si aucun menu n'est sélectionné, la catégorie apparaît dans tous les menus.</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {menus.map(menu => (
                    <button
                      key={menu.id}
                      type="button"
                      onClick={() => toggleCategoryMenu(menu.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                        categoryForm.menu_ids.includes(menu.id)
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                      }`}
                    >
                      <BookOpen className="inline h-3 w-3 mr-1 -mt-0.5" />
                      {menu.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={saveCategory}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="save-category-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCategory ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Daily Special Dialog */}
      <Dialog open={dailyDialogOpen} onOpenChange={setDailyDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5 text-amber-500" />
              {dailySpecial ? 'Modifier le menu du jour' : 'Créer un menu du jour'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* AI Suggestion */}
            <Button
              type="button"
              variant="outline"
              onClick={suggestDaily}
              disabled={suggestingDaily}
              className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="ai-suggest-daily-btn"
            >
              {suggestingDaily ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              {suggestingDaily ? 'L\'IA réfléchit...' : 'Suggestion IA'}
            </Button>

            <div className="space-y-2">
              <Label htmlFor="daily-title">Titre *</Label>
              <Input
                id="daily-title"
                value={dailyForm.title}
                onChange={(e) => setDailyForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Menu Découverte, Formule Express..."
                data-testid="daily-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="daily-desc">Description</Label>
              <Textarea
                id="daily-desc"
                value={dailyForm.description}
                onChange={(e) => setDailyForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Entrée + Plat + Dessert, servi de 12h à 14h30..."
                rows={2}
                data-testid="daily-desc-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="daily-price">Prix fixe (€)</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="daily-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={dailyForm.price}
                  onChange={(e) => setDailyForm(prev => ({ ...prev, price: e.target.value }))}
                  className="pl-9"
                  placeholder="15.00"
                  data-testid="daily-price-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Plats inclus</Label>
              <div className="grid grid-cols-1 gap-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
                {dishes.map((dish) => (
                  <div key={dish.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`daily-dish-${dish.id}`}
                      checked={dailyForm.dish_ids.includes(dish.id)}
                      onCheckedChange={() => toggleDailyDish(dish.id)}
                    />
                    <label
                      htmlFor={`daily-dish-${dish.id}`}
                      className="text-sm text-gray-700 cursor-pointer flex-1 flex justify-between"
                    >
                      <span>{dish.name}</span>
                      <span className="text-gray-400">{dish.price.toFixed(2)}€</span>
                    </label>
                  </div>
                ))}
              </div>
              {dailyForm.dish_ids.length > 0 && (
                <p className="text-xs text-amber-600">
                  {dailyForm.dish_ids.length} plat(s) sélectionné(s)
                  {dailyForm.dish_ids.length > 0 && !dailyForm.price && (
                    <span className="text-gray-400"> · Total à la carte : {dishes.filter(d => dailyForm.dish_ids.includes(d.id)).reduce((s, d) => s + d.price, 0).toFixed(2)}€</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={saveDaily}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="save-daily-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {dailySpecial ? 'Mettre à jour' : 'Publier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Dialog */}
      <Dialog open={menuDialogOpen} onOpenChange={setMenuDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingMenu ? 'Modifier le menu' : 'Nouveau menu'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="menu-name">Nom du menu *</Label>
              <Input
                id="menu-name"
                value={menuForm.name}
                onChange={(e) => setMenuForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Menu du Midi, Menu du Soir, Brunch..."
                data-testid="menu-name-input"
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-500" />
                  Créneaux horaires
                </Label>
                <Button type="button" variant="ghost" size="sm" onClick={addTimeSlot} className="text-orange-500 h-8">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
              <p className="text-xs text-gray-500">Définissez quand ce menu est actif. Le client verra automatiquement le bon menu.</p>
              {menuForm.time_slots.map((slot, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Créneau {idx + 1}</span>
                    {menuForm.time_slots.length > 1 && (
                      <button onClick={() => removeTimeSlot(idx)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS_OPTIONS.map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleSlotDay(idx, day.value)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                          slot.days.includes(day.value) 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-white text-gray-500 border border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={slot.start_hour}
                      onChange={(e) => updateTimeSlot(idx, 'start_hour', e.target.value)}
                      className="w-28 text-sm"
                    />
                    <span className="text-gray-400 text-sm">à</span>
                    <Input
                      type="time"
                      value={slot.end_hour}
                      onChange={(e) => updateTimeSlot(idx, 'end_hour', e.target.value)}
                      className="w-28 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={saveMenu}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="save-menu-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingMenu ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dish Dialog */}
      <Dialog open={dishDialogOpen} onOpenChange={setDishDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingDish ? 'Modifier le plat' : 'Nouveau plat'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dish-name">Nom du plat *</Label>
              <Input
                id="dish-name"
                value={dishForm.name}
                onChange={(e) => setDishForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Burger Classic, Pizza Margherita..."
                data-testid="dish-name-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="dish-desc">Description</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateDescription}
                  disabled={generatingDescription || !dishForm.name}
                  className="h-8 text-xs text-orange-500 hover:text-orange-600"
                  data-testid="generate-desc-btn"
                >
                  {generatingDescription ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-1" />
                  )}
                  Générer avec l'IA
                </Button>
              </div>
              <Textarea
                id="dish-desc"
                value={dishForm.description}
                onChange={(e) => setDishForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description appétissante du plat..."
                rows={3}
                data-testid="dish-desc-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dish-price">Prix sur place (€) *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="dish-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={dishForm.price}
                    onChange={(e) => setDishForm(prev => ({ ...prev, price: e.target.value }))}
                    className="pl-9"
                    placeholder="12.50"
                    data-testid="dish-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dish-price-takeaway">Prix à emporter (€)</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="dish-price-takeaway"
                    type="number"
                    step="0.01"
                    min="0"
                    value={dishForm.price_takeaway}
                    onChange={(e) => setDishForm(prev => ({ ...prev, price_takeaway: e.target.value }))}
                    className="pl-9"
                    placeholder="10.50"
                    data-testid="dish-price-takeaway-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dish-category">Catégorie *</Label>
              <Select 
                value={dishForm.category_id} 
                onValueChange={(value) => setDishForm(prev => ({ ...prev, category_id: value }))}
              >
                <SelectTrigger data-testid="dish-category-select">
                  <SelectValue placeholder="Choisir" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {menus.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-orange-500" />
                  Menus
                </Label>
                <p className="text-xs text-gray-500">Si aucun menu n'est sélectionné, le plat suit la catégorie.</p>
                <div className="flex flex-wrap gap-2">
                  {menus.map(menu => (
                    <button
                      key={menu.id}
                      type="button"
                      onClick={() => toggleDishMenu(menu.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                        dishForm.menu_ids.includes(menu.id)
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                      }`}
                      data-testid={`dish-menu-${menu.id}`}
                    >
                      {menu.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Photo du plat</Label>
              <Tabs defaultValue="upload" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload" className="text-sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Importer
                  </TabsTrigger>
                  <TabsTrigger value="url" className="text-sm">
                    <LinkIcon className="h-4 w-4 mr-2" />
                    URL
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="upload" className="mt-3">
                  <ImageUpload
                    value={dishForm.photo_url}
                    onChange={(url) => setDishForm(prev => ({ ...prev, photo_url: url }))}
                    aspectRatio="landscape"
                    placeholder="Cliquez ou glissez une photo"
                  />
                </TabsContent>
                <TabsContent value="url" className="mt-3 space-y-2">
                  <Input
                    id="dish-photo"
                    value={dishForm.photo_url}
                    onChange={(e) => setDishForm(prev => ({ ...prev, photo_url: e.target.value }))}
                    placeholder="https://..."
                    data-testid="dish-photo-input"
                  />
                  {dishForm.photo_url && !dishForm.photo_url.startsWith('/api/') && (
                    <img 
                      src={dishForm.photo_url} 
                      alt="Preview" 
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Allergens selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Allergènes
              </Label>
              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                {Object.entries(allergensList).map(([code, label]) => (
                  <div key={code} className="flex items-center space-x-2">
                    <Checkbox
                      id={`allergen-${code}`}
                      checked={dishForm.allergens.includes(code)}
                      onCheckedChange={() => toggleAllergen(code)}
                    />
                    <label
                      htmlFor={`allergen-${code}`}
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
              {dishForm.allergens.length > 0 && (
                <p className="text-xs text-amber-600">
                  {dishForm.allergens.length} allergène(s) sélectionné(s)
                </p>
              )}
            </div>

            {/* Upsells selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4 text-emerald-500" />
                  Suggestions "Vous aimerez aussi"
                </Label>
                {editingDish && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateUpsells}
                    disabled={generatingUpsells}
                    className="text-purple-600 hover:text-purple-700 h-8"
                  >
                    {generatingUpsells ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="h-4 w-4 mr-1" />
                        Suggérer par IA
                      </>
                    )}
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                {dishes.filter(d => d.id !== editingDish?.id).map((dish) => (
                  <div key={dish.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`upsell-${dish.id}`}
                      checked={dishForm.upsells.includes(dish.id)}
                      onCheckedChange={() => toggleUpsell(dish.id)}
                    />
                    <label
                      htmlFor={`upsell-${dish.id}`}
                      className="text-sm text-gray-700 cursor-pointer flex-1 flex justify-between"
                    >
                      <span>{dish.name}</span>
                      <span className="text-gray-400">{dish.price.toFixed(2)}€</span>
                    </label>
                  </div>
                ))}
                {dishes.filter(d => d.id !== editingDish?.id).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    Ajoutez d'autres plats pour créer des suggestions
                  </p>
                )}
              </div>
              {dishForm.upsells.length > 0 && (
                <p className="text-xs text-emerald-600">
                  {dishForm.upsells.length} suggestion(s) sélectionnée(s)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dish-margin">Marge (€) - privé, pour l'IA</Label>
              <Input
                id="dish-margin"
                type="number"
                step="0.01"
                min="0"
                value={dishForm.margin}
                onChange={(e) => setDishForm(prev => ({ ...prev, margin: e.target.value }))}
                placeholder="Optionnel - aide l'IA à recommander"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="dish-available">Disponible</Label>
                <p className="text-xs text-gray-500">Afficher ce plat sur le menu public</p>
              </div>
              <Switch 
                id="dish-available"
                checked={dishForm.available}
                onCheckedChange={(checked) => setDishForm(prev => ({ ...prev, available: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={saveDish}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="save-dish-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingDish ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Menu Import Dialog */}
      <MenuImport
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        menus={menus}
        onImportComplete={fetchData}
      />
    </div>
  );
};

export default MenuManagement;
